import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, ArrowUpRight, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import SiteLogo from "./SiteLogo";

function FooterSection({ title, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#fbd9e5]/15 md:border-0" data-testid={testid}>
      {/* Mobile: button with chevron */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="md:hidden flex w-full items-center justify-between py-4 text-left"
        aria-expanded={open}
      >
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#fbd9e5]/60 font-semibold">
          {title}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[#fbd9e5]/60 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {/* Desktop: always visible title */}
      <p className="hidden md:block text-[11px] uppercase tracking-[0.3em] text-[#fbd9e5]/50">
        {title}
      </p>

      {/* Content — collapsible on mobile, always shown on desktop */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden"
          >
            <div className="pb-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="hidden md:block mt-5">{children}</div>
    </div>
  );
}

export default function Footer() {
  const { t, contactInfo } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden bg-[#3e2723] text-[#fbd9e5]" data-testid="footer">
      <div aria-hidden className="pointer-events-none absolute -top-40 -right-20 h-[480px] w-[480px] rounded-full bg-[#fbd9e5]/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute bottom-0 -left-32 h-[360px] w-[360px] rounded-full bg-[#fbd9e5]/5 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-16 sm:pt-24 pb-6">
        {/* Brand row (always visible) */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="max-w-md">
            <Link to="/" className="inline-flex items-center">
              <SiteLogo variant="footer" />
            </Link>
            <p className="mt-4 text-[14px] leading-relaxed text-[#fbd9e5]/75">
              {t.footer.tagline}
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a
                href={`mailto:${contactInfo.email}`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#fbd9e5]/20 hover:bg-[#fbd9e5] hover:text-[#3e2723] transition-colors"
                data-testid="footer-mail"
                aria-label="Email"
              >
                <Mail className="h-3.5 w-3.5" />
              </a>
              <a
                href={`https://instagram.com/${contactInfo.instagram.replace("@", "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#fbd9e5]/20 hover:bg-[#fbd9e5] hover:text-[#3e2723] transition-colors"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram className="h-3.5 w-3.5" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#fbd9e5]/20 hover:bg-[#fbd9e5] hover:text-[#3e2723] transition-colors"
                data-testid="footer-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>

        {/* Links grid — collapsible on mobile, 3 columns on desktop */}
        <div className="mt-10 grid gap-0 md:grid-cols-3 md:gap-12">
          <FooterSection title={t.footer.explore} testid="footer-section-explore">
            <ul className="space-y-2.5 text-[14px]">
              <li><Link to="/" className="hover:text-white transition-colors">{t.nav.home}</Link></li>
              <li><Link to="/services" className="hover:text-white transition-colors">{t.nav.services}</Link></li>
              <li><Link to="/portfolio" className="hover:text-white transition-colors">{t.nav.portfolio}</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">{t.nav.about}</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">{t.nav.contact}</Link></li>
            </ul>
          </FooterSection>

          <FooterSection title={t.footer.services_title} testid="footer-section-services">
            <ul className="space-y-2.5 text-[14px] text-[#fbd9e5]/80">
              {t.footer.services_list.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </FooterSection>

          <FooterSection title={t.footer.contact_title} testid="footer-section-contact">
            <ul className="space-y-2.5 text-[14px]">
              <li>
                <a href={`mailto:${contactInfo.email}`} className="inline-flex items-center gap-1 hover:text-white">
                  {contactInfo.email}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </li>
              <li className="text-[#fbd9e5]/80">{contactInfo.phone}</li>
              <li className="text-[#fbd9e5]/80">București, RO</li>
            </ul>
          </FooterSection>
        </div>

        {/* Centered brand wordmark — SVG scales perfectly on every device */}
        <div aria-hidden className="mt-14 sm:mt-20 w-full">
          <svg
            viewBox="0 0 1000 140"
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-auto"
            aria-hidden
          >
            <text
              x="500"
              y="108"
              textAnchor="middle"
              fill="#fbd9e5"
              fillOpacity="0.14"
              style={{
                fontFamily: "Cabinet Grotesk, Manrope, sans-serif",
                fontWeight: 900,
                fontSize: "140px",
                letterSpacing: "-6px",
              }}
            >
              SOCIAL DESTINY
            </text>
          </svg>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t border-[#fbd9e5]/15 pt-5 text-[12px] text-[#fbd9e5]/60">
          <p>© {year} Social Destiny. {t.footer.rights}</p>
          <p>{t.footer.made_with}</p>
        </div>
      </div>
    </footer>
  );
}
